import {ServiceBase} from '../ServiceBase';
import {ILanguageData, ILanguageService} from './language.service.interface';
import { action, observable, makeObservable } from 'mobx';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {TranslatedText} from './translated-text';

import {LanguageSwitcherStructure} from './language-switcher/language-switcher-structure';
import {LanguageSwitcherItem} from './language-switcher/language-switcher-item';
import {LanguageSwitcherSection} from './language-switcher/language-switcher-section';
import {SelectedLanguageSwitcherItem} from './language-switcher/selected-language-switcher-item';
import {ILanguageSwitcherItem} from './language-switcher/language-switcher-item.interface';
import {
  LanguageSwitcherConfiguration,
  LanguageSwitcherItemConfiguration, LanguageSwitcherSectionConfiguration
} from '../../app-bootstrap/config-interfaces/b2c-startup.configurations';

export class LanguageService extends ServiceBase implements ILanguageService {
  constructor(private _defaultLanguageData: ILanguageData, services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);

    this._loadLanguageData();

    if (this._isLanguageSwitcherEnabled) {
      this._setLanguageSwitcher(this.services.configuration.startupConfiguration.b2c.languageSwitcher);
    } else {
      this._setLanguageSwitcher(null);
    }


    this.reaction(() => this.services.location.currentUri,
      (uri) => {
      this._updateLanguageSwitcher(uri.pathName.toString());
    }, {
      fireImmediately: this.languageSwitcher.isEmpty
    });
  }

  private get _isLanguageSwitcherEnabled(): boolean {
    return Boolean(this.services.configuration.data.languageSwitcher?.display)
  }


  private _lastUrlWithoutTheFragmentQueriedForLanguageSwitcher = '';

  public get isRTLLanguage(): boolean {
    return this.services.dom.getElementsByTagName('html')[0].classList.contains('cgp-rtl');
  }

  get currentLanguage(): string {
    return this.services.configuration.startupConfiguration.b2c.language.iso3;
  }

  get currentLanguageIso2(): string {
    return this.services.configuration.startupConfiguration.b2c.language.iso2;
  }


  private _getDefaultTexts(): TranslatedText {
    return this._defaultLanguageData.text || {};
  }


  private _loadLanguageData(): void {
    let textOverrides = this.services.configuration.startupConfiguration.orbit.lang?.text;
    let translatedTexts = {...this._getDefaultTexts(), ...textOverrides};

    this._setTranslatedTexts(translatedTexts);
  }


  @observable.ref
  translatedTexts: TranslatedText = {};

  @action.bound
  private _setTranslatedTexts(data: TranslatedText): void {
    this.translatedTexts = data;
  }

  @observable.ref
  languageSwitcher: LanguageSwitcherStructure;

  @action.bound
  private _setLanguageSwitcher(langSwitcherConfig: LanguageSwitcherConfiguration): void {
    let languagesSection = this._extractLangSwitcherSection(langSwitcherConfig && langSwitcherConfig.languages, langSwitcherConfig && langSwitcherConfig.languageSettings, 'CHOOSE_LANG');

    let countriesSection = this._extractLangSwitcherSection(langSwitcherConfig && langSwitcherConfig.countries, langSwitcherConfig && langSwitcherConfig.countrySettings, 'CHOOSE_COUNTRY')

    this.languageSwitcher = {
      languagesSection: languagesSection,
      countriesSection: countriesSection,
      isEmpty: languagesSection.items.length === 0 && countriesSection.items.length === 0
    };
  }

  private _extractLangSwitcherSection(itemsConfig: LanguageSwitcherItemConfiguration[], sectionConfig: LanguageSwitcherSectionConfiguration, fallbackTitleKey: string): LanguageSwitcherSection {
    itemsConfig = itemsConfig || [];
    sectionConfig = sectionConfig || {title: '', current: ''};

    let items: ILanguageSwitcherItem[] = [];

    if(sectionConfig.current && itemsConfig.length > 0) {
      items.push(new SelectedLanguageSwitcherItem(sectionConfig.current));
    }

    itemsConfig.forEach(cfg => {
      items.push(new LanguageSwitcherItem(cfg, this.services));
    });

    return new LanguageSwitcherSection(this.services,
                                      sectionConfig.title,
                                      fallbackTitleKey,
                                      items);

  }

  _updateLanguageSwitcher(url: string) {
    if (this.languageSwitcher.isEmpty) {
      return;
    }

    if(this._lastUrlWithoutTheFragmentQueriedForLanguageSwitcher !== url && !this.services.device.hybrid.isEmbedded) {
      this._lastUrlWithoutTheFragmentQueriedForLanguageSwitcher = url;

      this.services.cmsApi
          .getLanguageSwitcherConfiguration(url)
          .then(config => {
            if (config) { this._setLanguageSwitcher(config); }
          });
    }
  }
}
